import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import ComplianceCheckboxes from "@/components/ComplianceCheckboxes";
import { toast } from "sonner";

export default function Cart() {
  const { items, setQty, remove, subtotal, clear } = useCart();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [compliant, setCompliant] = useState(false);
  const [method, setMethod] = useState("stripe");
  const [discountCode, setDiscountCode] = useState("");
  const [preview, setPreview] = useState(null);
  const nl = lang === "nl";
  const de = lang === "de";
  const L = {
    fillEmail: nl ? "Vul naam en e-mail in" : de ? "Name und E-Mail eingeben" : "Please enter name and email",
    acceptDecl: nl ? "Accepteer alle vereiste verklaringen" : de ? "Bitte alle erforderlichen Erklärungen akzeptieren" : "Please accept all required declarations",
    payFail: nl ? "Betaling mislukt" : de ? "Zahlung fehlgeschlagen" : "Payment failed",
    confirm: nl ? "Bevestig bestelling →" : de ? "Bestellung bestätigen →" : "Confirm order →",
    tickDecl: nl ? "Vink verklaringen aan" : de ? "Erklärungen ankreuzen" : "Tick declarations",
    payMethod: nl ? "BETAALMETHODE" : de ? "ZAHLUNGSMETHODE" : "PAYMENT METHOD",
    bankTitle: nl ? "Bankoverschrijving" : de ? "Banküberweisung" : "Bank transfer",
    bankDesc: nl
      ? "iDEAL · Bunq · SEPA · handmatig overmaken via je bank app. Werkt vandaag al."
      : de
      ? "iDEAL · Bunq · SEPA · manuell über Ihre Banking-App. Sofort einsatzbereit."
      : "iDEAL · Bunq · SEPA · manual transfer via your banking app. Works today.",
    stripeTitle: nl ? "Creditcard, iDEAL, PayPal, Klarna" : de ? "Kreditkarte, iDEAL, PayPal, Klarna" : "Card, iDEAL, PayPal, Klarna",
    stripeDesc: nl
      ? "Direct betalen via Stripe. Order wordt meteen bevestigd en vandaag verzonden bij bestelling voor 15:00."
      : de
      ? "Direkt zahlen über Stripe. Ihre Bestellung wird sofort bestätigt und heute versendet bei Bestellung bis 15:00."
      : "Pay instantly via Stripe. Order is confirmed immediately and ships today if placed before 3PM.",
    secure: nl ? "Veilig · SSL · GDPR compliant" : de ? "Sicher · SSL · DSGVO-konform" : "Secure · SSL · GDPR compliant",
    freeRemainPrefix: nl ? "Nog " : de ? "Noch " : "Add ",
    freeRemainSuffix: nl ? " voor gratis verzending" : de ? " für kostenlosen Versand" : " more for free shipping",
    freeUnlocked: nl ? "✓ Gratis verzending vrijgespeeld" : de ? "✓ Kostenloser Versand freigeschaltet" : "✓ Free shipping unlocked",
    shipAddress: nl ? "VERZENDADRES" : de ? "LIEFERADRESSE" : "SHIPPING ADDRESS",
    fullName: nl ? "Volledige naam" : de ? "Vollständiger Name" : "Full name",
    email: nl ? "E-mail" : de ? "E-Mail" : "Email",
    streetNum: nl ? "Straat & nummer" : de ? "Straße & Nr." : "Street & number",
    postal: nl ? "Postcode" : de ? "PLZ" : "Postal code",
    city: nl ? "Plaats" : de ? "Stadt" : "City",
    summary: nl ? "SAMENVATTING" : de ? "ZUSAMMENFASSUNG" : "SUMMARY",
    freeStr: nl ? "GRATIS" : de ? "KOSTENLOS" : "FREE",
    calcNext: nl ? "BEREKEND IN VOLGENDE STAP" : de ? "IM NÄCHSTEN SCHRITT BERECHNET" : "CALCULATED AT NEXT STEP",
    remove: nl ? "VERWIJDER" : de ? "ENTFERNEN" : "REMOVE",
  };
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    address: "",
    city: "",
    postal: "",
    country: "NL",
  });

  // Real-time price preview: recompute total whenever cart / code / email changes
  useEffect(() => {
    if (!items.length) { setPreview(null); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/checkout/preview", {
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
          email: form.email || null,
          discount_code: discountCode || null,
        });
        setPreview(data);
      } catch { setPreview(null); }
    }, 400);
    return () => clearTimeout(timer);
  }, [items, discountCode, form.email]);


  const checkout = async () => {
    if (!items.length) return;
    if (!form.name || !form.email) {
      toast.error(L.fillEmail);
      return;
    }
    if (!compliant) {
      toast.error(L.acceptDecl);
      return;
    }
    setLoading(true);
    try {
      const body = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping_name: form.name,
        shipping_email: form.email,
        shipping_address: form.address,
        shipping_city: form.city,
        shipping_postal: form.postal,
        shipping_country: form.country,
        discount_code: discountCode || null,
      };
      if (method === "bank_transfer") {
        const { data } = await api.post("/checkout/bank-transfer", body);
        clear();
        navigate(`/checkout/instructions/${data.reference}`);
      } else {
        const { data } = await api.post("/checkout/session", body);
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || L.payFail);
      setLoading(false);
    }
  };

  return (
    <div data-testid="cart-page" className="pt-32 pb-24 bg-[#F7F7F6] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="font-mono-label text-[11px] text-neutral-500 mb-8">[ CART / CHECKOUT ]</div>
        <h1 className="font-serif-display font-light text-5xl md:text-7xl mb-16">{t("cart_title")}</h1>

        {items.length === 0 ? (
          <div className="border border-neutral-300 p-16 text-center bg-white">
            <p className="font-serif-display text-3xl mb-6">{t("cart_empty")}</p>
            <Link to="/shop" data-testid="cart-continue" className="btn-sharp">{t("cart_continue")}</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="border-t border-neutral-300">
                {items.map((i) => (
                  <div key={i.product_id} data-testid={`cart-item-${i.slug}`} className="border-b border-neutral-300 py-6 flex flex-col md:grid md:grid-cols-12 gap-4 md:items-center">
                    <div className="flex gap-4 md:contents">
                      <div className="w-20 md:w-auto md:col-span-2">
                        <div className="spotlight aspect-square bg-white flex items-center justify-center">
                          <img src={i.image_url} alt={i.name} className="max-h-full max-w-full object-contain" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 md:col-span-5">
                        <p className="font-serif-display text-xl md:text-2xl leading-tight">{i.name}</p>
                        <p className="font-mono-label text-[10px] text-neutral-500 mt-1">{i.dosage}</p>
                        <p className="md:hidden font-serif-display text-xl mt-2">€{(i.price * i.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between md:contents">
                      <div className="flex items-center border border-black" data-testid={`cart-qty-${i.product_id}`}>
                        <button type="button" onClick={() => setQty(i.product_id, i.quantity - 1)} className="min-w-[44px] min-h-[44px] px-3 py-2 font-mono-label text-base active:bg-black active:text-[#F7F7F6] hover:bg-black hover:text-[#F7F7F6] transition-colors select-none touch-manipulation">−</button>
                        <span className="px-4 min-w-[40px] text-center font-mono-label text-sm select-none">{i.quantity}</span>
                        <button type="button" onClick={() => setQty(i.product_id, i.quantity + 1)} className="min-w-[44px] min-h-[44px] px-3 py-2 font-mono-label text-base active:bg-black active:text-[#F7F7F6] hover:bg-black hover:text-[#F7F7F6] transition-colors select-none touch-manipulation">+</button>
                      </div>
                      <div className="md:col-span-2 md:text-right">
                        <p className="hidden md:block font-serif-display text-xl">€{(i.price * i.quantity).toFixed(2)}</p>
                        <button onClick={() => remove(i.product_id)} className="font-mono-label text-[10px] text-neutral-500 hover:text-black md:mt-2">{L.remove}</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="font-mono-label text-[11px] text-neutral-500 mt-12 mb-4">[ {L.shipAddress} ]</h2>
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="ship-name" placeholder={L.fullName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2 border border-neutral-300 bg-white px-4 py-3 font-body" />
                <input data-testid="ship-email" placeholder={L.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="col-span-2 border border-neutral-300 bg-white px-4 py-3 font-body" />
                <input data-testid="ship-address" placeholder={L.streetNum} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="col-span-2 border border-neutral-300 bg-white px-4 py-3 font-body" />
                <input data-testid="ship-postal" placeholder={L.postal} value={form.postal} onChange={(e) => setForm({ ...form, postal: e.target.value })} className="border border-neutral-300 bg-white px-4 py-3 font-body" />
                <input data-testid="ship-city" placeholder={L.city} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="border border-neutral-300 bg-white px-4 py-3 font-body" />
              </div>

              <ComplianceCheckboxes onValidChange={setCompliant} />
            </div>

            <div className="lg:col-span-4">
              <div className="border border-neutral-300 bg-white p-8 sticky top-24">
                <p className="font-mono-label text-[11px] text-neutral-500 mb-6">[ {L.summary} ]</p>

                {/* Free shipping progress bar */}
                {(() => {
                  const FREE = 200;
                  const remaining = Math.max(0, FREE - subtotal);
                  const pct = Math.min(100, (subtotal / FREE) * 100);
                  return (
                    <div className="mb-6" data-testid="free-ship-bar">
                      <div className="flex justify-between font-mono-label text-[10px] mb-2">
                        <span className="text-neutral-700">
                          {remaining > 0 ? (
                            <>{L.freeRemainPrefix}<span className="text-black font-semibold">€{remaining.toFixed(2)}</span>{L.freeRemainSuffix}</>
                          ) : (
                            <span className="text-emerald-700 font-semibold">{L.freeUnlocked}</span>
                          )}
                        </span>
                        <span className="text-neutral-500">€200</span>
                      </div>
                      <div className="h-1.5 w-full bg-neutral-200 relative overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 transition-all duration-500"
                          style={{
                            width: `${pct}%`,
                            background: remaining > 0
                              ? "linear-gradient(90deg,#00529B,#5A2A82)"
                              : "linear-gradient(90deg,#10b981,#007A78)",
                          }}
                        />
                      </div>
                    </div>
                  );
                })()}

                <div className="flex justify-between py-3 border-b border-neutral-300">
                  <span>{t("cart_subtotal")}</span>
                  <span className="font-serif-display text-xl">€{subtotal.toFixed(2)}</span>
                </div>

                {/* Discount code + VIP + Sale */}
                <div className="py-3 border-b border-neutral-300 space-y-2">
                  <div className="flex gap-2" data-testid="discount-input-row">
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder={nl ? "KORTINGSCODE" : de ? "RABATTCODE" : "PROMO CODE"}
                      className="flex-1 border border-neutral-300 bg-white px-3 py-2 font-mono text-xs uppercase tracking-wider"
                      data-testid="discount-code-input"
                    />
                    {discountCode && (
                      <button
                        type="button"
                        onClick={() => setDiscountCode("")}
                        className="font-mono-label text-[10px] text-neutral-500 underline"
                      >
                        {nl ? "WIS" : de ? "LÖSCHEN" : "CLEAR"}
                      </button>
                    )}
                  </div>
                  {preview && preview.discount_pct > 0 && (
                    <div className="flex justify-between text-sm" data-testid="discount-applied">
                      <span className="text-emerald-700 flex items-center gap-1.5">
                        {preview.discount_source === "vip" && (
                          <span className="font-mono-label text-[9px] px-1.5 py-0.5 bg-amber-100 border border-amber-400 text-amber-800">
                            ★ VIP
                          </span>
                        )}
                        {preview.discount_source === "sale" && (
                          <span className="font-mono-label text-[9px] px-1.5 py-0.5 bg-red-100 border border-red-400 text-red-800">
                            SALE
                          </span>
                        )}
                        {preview.discount_source?.startsWith("code") && (
                          <span className="font-mono-label text-[9px] px-1.5 py-0.5 bg-blue-100 border border-blue-400 text-blue-800">
                            {preview.code_used}
                          </span>
                        )}
                        <span>−{preview.discount_pct}% korting</span>
                      </span>
                      <span className="font-serif-display text-lg text-emerald-700">−€{preview.discount_amount.toFixed(2)}</span>
                    </div>
                  )}
                  {discountCode && preview && preview.discount_source === "none" && (
                    <p className="font-mono-label text-[10px] text-[#D12A2F]">
                      {nl ? "Code ongeldig of verlopen" : de ? "Code ungültig oder abgelaufen" : "Code invalid or expired"}
                    </p>
                  )}
                </div>
                <div className="flex justify-between py-3 border-b border-neutral-300">
                  <span>{t("cart_shipping")}</span>
                  <span className="font-mono-label text-[11px] text-neutral-500">
                    {subtotal >= 200 ? L.freeStr : L.calcNext}
                  </span>
                </div>
                <div className="flex justify-between py-4 items-baseline">
                  <span className="font-serif-display text-2xl">{t("cart_total")}</span>
                  <span data-testid="cart-total" className="font-serif-display text-3xl">€{(preview?.total ?? subtotal).toFixed(2)}</span>
                </div>

                <button data-testid="cart-checkout" onClick={checkout} disabled={loading || !compliant} className="btn-solid w-full justify-center mt-6 disabled:opacity-40 disabled:cursor-not-allowed">
                  {loading ? "…" : compliant ? (method === "bank_transfer" ? L.confirm : `${t("cart_checkout")} →`) : L.tickDecl}
                </button>

                {/* Payment method selector */}
                <div data-testid="payment-method-picker" className="mt-6 border-t border-neutral-300 pt-4">
                  <p className="font-mono-label text-[10px] text-neutral-500 mb-3">[ {L.payMethod} ]</p>
                  <div className="space-y-2">
                    <label className={`flex items-start gap-3 p-3 border-2 cursor-pointer transition-colors ${method === "stripe" ? "border-black bg-white" : "border-neutral-300 bg-white/60 hover:border-neutral-500"}`}>
                      <input
                        type="radio"
                        name="pm"
                        value="stripe"
                        checked={method === "stripe"}
                        onChange={() => setMethod("stripe")}
                        className="mt-1 accent-black"
                        data-testid="pm-stripe"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold">{L.stripeTitle}</p>
                          <span className="font-mono-label text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">{nl ? "AANBEVOLEN" : de ? "EMPFOHLEN" : "RECOMMENDED"}</span>
                        </div>
                        <p className="text-[11px] text-neutral-600 leading-snug">
                          {L.stripeDesc}
                        </p>
                        <div className="mt-2 flex gap-1 flex-wrap">
                          {["iDEAL","VISA","MASTERCARD","AMEX","PAYPAL","KLARNA","BANCONTACT"].map((m)=>(
                            <span key={m} className="border border-neutral-300 px-1.5 py-0.5 font-mono-label text-[9px]">{m}</span>
                          ))}
                        </div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 p-3 border-2 cursor-pointer transition-colors ${method === "bank_transfer" ? "border-black bg-white" : "border-neutral-300 bg-white/60 hover:border-neutral-500"}`}>
                      <input
                        type="radio"
                        name="pm"
                        value="bank_transfer"
                        checked={method === "bank_transfer"}
                        onChange={() => setMethod("bank_transfer")}
                        className="mt-1 accent-black"
                        data-testid="pm-bank-transfer"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-semibold">{L.bankTitle}</p>
                        <p className="text-[11px] text-neutral-600 leading-snug">
                          {L.bankDesc}
                        </p>
                        <div className="mt-2 flex gap-1 flex-wrap">
                          {["BUNQ","SEPA","QR"].map((m)=>(
                            <span key={m} className="border border-neutral-300 px-1.5 py-0.5 font-mono-label text-[9px]">{m}</span>
                          ))}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <p className="mt-4 font-mono-label text-[9px] text-neutral-500">
                  {L.secure}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
